import { ApiError, ORIGINAL_STATUS_HEADER, intendedStatus } from '@yrp/shared/errors';

/**
 * The error every dashboard fetch wrapper throws for a non-2xx response.
 * Kept apart from `api.ts` (which reads the Vite env) so it runs under
 * `node --test`.
 */
export type ApiFetchError = Error & {
  statusCode: number;
  code?: string;
  body?: unknown;
};

export async function apiErrorFromResponse(res: Response): Promise<ApiFetchError> {
  const body = await safeJson(res);
  const parsed = ApiError.safeParse(body);
  // The status the API meant: a 502/503/504 travels as a 424 so the edge
  // lets the body through. Every `statusCode === 503` "connect Microsoft"
  // check downstream relies on getting the 503 back here.
  const status = intendedStatus(res.status, res.headers.get(ORIGINAL_STATUS_HEADER));
  const message = parsed.success ? parsed.data.message : `HTTP ${status}`;
  const err = new Error(message) as ApiFetchError;
  err.statusCode = status;
  if (parsed.success) err.code = parsed.data.code;
  // Keep the raw payload: some errors carry structured data the caller
  // needs to act on (e.g. a 409 from a group send returns the live
  // recipient roster so the UI can re-confirm against it).
  err.body = body;
  return err;
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
